import { useMemo } from 'react';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { Autocomplete, Breadcrumbs, Checkbox, Prompt, Typography } from '@weareopenr/component-library';
import { getHierarchyNames } from 'src/helpers';
import { filterValidMoveToCategories } from './utilities';
import { IVenueCategory } from 'src/api/getVenueCategories/types';
import { IPricingCategory } from 'src/api/getPricingCategories/types';
import styles from './MoveToCategoryPrompt.module.scss';

const MAX_BREADCRUMB_LEVEL = 2;
const NO_PARENT_VALUE = 'no-parent';

export type TMoveToCategoryItem = IVenueCategory | IPricingCategory;

interface MoveToCategoryPromptProps<T extends TMoveToCategoryItem> {
  open: boolean;
  categoryName: string;
  orderedCategories: T[];
  category: T;
  onClose: () => void;
  onSubmit: (newParentId: string | null) => void;
  isSubmitting?: boolean;
  confirmationMessage?: string;
  subtitle?: string;
}

export const MoveToCategoryPrompt = <T extends TMoveToCategoryItem>({
  open,
  categoryName,
  orderedCategories,
  category,
  onClose,
  onSubmit: onSubmitCallback,
  isSubmitting = false,
  confirmationMessage,
  subtitle,
}: MoveToCategoryPromptProps<T>) => {
  const { handleSubmit, control, watch } = useForm<FieldValues>({
    defaultValues: { moveToCategory: null, confirmed: false },
  });

  const selectedCategory = watch('moveToCategory');
  const isConfirmed = watch('confirmed');

  const isButtonDisabled = !selectedCategory || (confirmationMessage && !isConfirmed);

  const categories = useMemo(() => {
    const validCategories = filterValidMoveToCategories(category, orderedCategories);

    return validCategories.map((cat) => ({
      ...cat,
      breadcrumbs: getHierarchyNames(cat, orderedCategories, {
        id: 'category_id',
        parent_id: 'parent_category_id',
      }).map((breadcrumb) => ({ label: breadcrumb })),
    }));
  }, [orderedCategories, category]);

  const onSubmit = (data: FieldValues) => {
    if (confirmationMessage && !data.confirmed) return;
    onSubmitCallback(data.moveToCategory === NO_PARENT_VALUE ? null : data.moveToCategory ?? null);
  };

  return (
    <Prompt
      promptHeading={`Move "${categoryName}" to a different category?`}
      closePrompt={onClose}
      open={open}
      size="small"
      backButton
      backButtonText="Cancel"
      buttonText="Save"
      buttonType="submit"
      buttonFormId="move-to-category-form"
      isSubmitting={isSubmitting}
      isButtonDisabled={isButtonDisabled}
    >
      <form id="move-to-category-form" className={styles['move-form']} onSubmit={handleSubmit(onSubmit)}>
        {subtitle && (
          <Typography size="medium" className={styles['subtitle']}>
            {subtitle}
          </Typography>
        )}
        <Controller
          name="moveToCategory"
          control={control}
          rules={{ required: 'Please select a category' }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <div className={styles['autocomplete']}>
              <Autocomplete
                name="moveToCategory"
                label="Choose a Parent Category"
                options={categories.map((cat) => ({
                  value: cat.category_id ?? NO_PARENT_VALUE,
                  label: cat.name,
                  content: (
                    <div className={styles['list-option']}>
                      <Breadcrumbs breadcrumbs={cat.breadcrumbs} maxLevel={MAX_BREADCRUMB_LEVEL} />
                    </div>
                  ),
                }))}
                value={value ?? undefined}
                onChange={(categoryId) => onChange(categoryId ?? null)}
                error={error?.message}
              />
            </div>
          )}
        />
        {confirmationMessage && (
          <Controller
            name="confirmed"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Checkbox checked={value} onChange={onChange} className={styles['confirmation-checkbox']}>
                <Typography size="medium" weight="regular" className={styles['confirmation-checkbox']}>
                  {confirmationMessage}
                </Typography>
              </Checkbox>
            )}
          />
        )}
      </form>
    </Prompt>
  );
};
